import React from 'react'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Stack from '@mui/material/Stack'

import { Icon, Text } from '@mxenabled/mxui'
import { ExpandMore } from '@mxenabled/mx-icons'

import { ANALYTICS_EVENTS, useEvent } from '../../../common'
import { type HelpType } from '../../../core'

interface HelpByCategory {
  guid: string
  icon: string
  title: string
  topics: Topic[]
}

interface HelpByCategoryListProps {
  helpByCategory: HelpByCategory[]
  onClick: (label: string, type: HelpType) => void
}

interface Topic {
  label: string
  type: HelpType
}

export const HelpByCategoryList: React.FC<HelpByCategoryListProps> = ({
  helpByCategory,
  onClick,
}) => {
  const [expanded, setExpanded] = React.useState<number | null>(null)

  const { onEvent } = useEvent()

  return (
    <Stack sx={{ flex: 1, gap: 12, p: 12 }}>
      {helpByCategory.map(({ icon, topics, title }, i) => (
        <Accordion
          disableGutters={true}
          expanded={i === expanded}
          key={i}
          onChange={(_, isExpanded) => {
            onEvent(ANALYTICS_EVENTS.HELP_EXPAND_CATEGORY, {
              category: title,
            })

            setExpanded(isExpanded ? i : null)
          }}
          sx={accordionStyles}
        >
          <AccordionSummary
            expandIcon={<ExpandMore sx={{ color: 'text.primary' }} />}
            sx={{ p: '14px 24px', height: 64 }}
          >
            <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 12 }}>
              <Icon name={icon} size={24} />
              <Text variant="body1">{title}</Text>
            </Stack>
          </AccordionSummary>
          <Divider />
          <AccordionDetails
            sx={{
              ml: 56,
              p: 0,
            }}
          >
            <List sx={{ '& .MuiListItemButton-root': { pl: 6 } }}>
              {topics.map(({ label, type }, i) => (
                <ListItemButton
                  divider={true}
                  key={i}
                  onClick={() => {
                    onEvent(ANALYTICS_EVENTS.HELP_CLICK_CATEGORY, {
                      category: title,
                      helpTopic: label,
                    })

                    onClick(label, type)
                  }}
                  sx={{
                    borderColor: 'neutral.light',
                    '&:last-child': {
                      border: 'none',
                      borderBottomRightRadius: 7,
                    },
                  }}
                >
                  <Text color="secondary" variant="body2">
                    {label}
                  </Text>
                </ListItemButton>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  )
}

const accordionStyles = (theme: any) => {
  return {
    border: `1px solid ${theme.palette.neutral?.light}`,
    borderRadius: '8px',
    '&:first-of-type': { borderRadius: '8px' },
    '&:before': {
      opacity: 0,
    },
    '& .MuiAccordion-region': {
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    },
  }
}
